import { useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import {
  signalDialValue,
  signalMono,
  signalNudgeButton
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  readNumberStepperAnswer,
  readNumberStepperConfig,
  stepBy
} from './numberStepper';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `number_stepper@1`: one bounded quantity.
 *
 * A headline-sized number with two pill buttons under it — the mobile prototype's dial.
 *
 * The number is the largest thing on the screen, larger than the question, because on this screen it
 * IS the answer. The adjust buttons are pills splitting the width rather than small circles: a 44px
 * target spanning half the screen can be hit without looking, which is the difference between
 * adjusting a number while walking and stopping to aim at a circle.
 *
 * The buttons disable at the ends of the range rather than silently refusing. A control that stops
 * responding is indistinguishable from a broken one; a disabled control says "you are at the limit"
 * without needing a sentence to explain it.
 *
 * Deliberately NOT a free text field with a number keyboard. The range and the step are real
 * constraints the server enforces, and a typed field invites values it will refuse — 37 in a
 * five-step range, a stray decimal — which the respondent only discovers at submit. Everything this
 * control can produce is already valid.
 *
 * The unit comes from `semantic.unit` and is displayed, never sent: the answer is the number, and
 * what it counts is a property of the question.
 *
 * **Untouched shows no number.** An earlier version displayed the minimum before anyone had acted,
 * which was wrong twice over: on a required question it left Next disabled beneath a control that
 * looked answered, with nothing on screen explaining why; and persisting that value on mount to fix
 * it would have been worse, recording an answer nobody gave — on an optional question, fabricated
 * data for everyone who skipped it. So the value reads as absent until the first tap, and the first
 * tap lands on the first legal value.
 */
export const NumberStepperBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readNumberStepperConfig(block), [block]);
  // null means "not answered yet" and is different from any number, including zero.
  const value = readNumberStepperAnswer(answer)?.value ?? null;

  const questionId = questionIdOf(block);

  // Decreasing from nothing is meaningless, so that button waits until there is a value to decrease.
  const atMin = value === null || (config.min !== null && value <= config.min);
  const atMax = value !== null && config.max !== null && value >= config.max;

  const move = (direction: 1 | -1) =>
    onChange({ value: stepBy(value, direction, config) });

  const rangeText = (): string => {
    const { min, max } = config;
    if (min !== null && max !== null) {
      return getTransWithParams('guestForm.number.range', { min, max });
    }
    if (min !== null) {
      return getTransWithParams('guestForm.number.atLeast', { min });
    }
    if (max !== null) {
      return getTransWithParams('guestForm.number.atMost', { max });
    }
    return '';
  };

  return (
    <Stack spacing={3}>
      {/* No derived hint: the range is printed under the number, where it sits beside the value it
          bounds and beside the buttons that stop at it. */}
      <BlockHeading block={block} locale={locale} />

      <Stack spacing={1.75}>
        {/* The value and its unit on one baseline, so "20" and "Stunden" read as one phrase rather
            than as a number with a label stuck underneath it. */}
        <Stack direction="row" alignItems="baseline" spacing={1.25}>
          <Typography
            component="div"
            role="spinbutton"
            aria-labelledby={questionId}
            aria-valuenow={value ?? undefined}
            aria-valuemin={config.min ?? undefined}
            aria-valuemax={config.max ?? undefined}
            // Without this a screen reader announces nothing at all for an untouched dial, where a
            // sighted user at least sees a dash.
            aria-valuetext={
              value === null ? getTrans('guestForm.number.empty') : undefined
            }
            aria-live="polite"
            data-testid="form-number-value"
            sx={signalDialValue}
          >
            {value ?? '—'}
          </Typography>
          {config.unit && (
            <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
              {config.unit}
            </Typography>
          )}
        </Stack>

        {rangeText() && (
          <Typography
            sx={{ ...signalMono, fontSize: 12, color: 'text.disabled' }}
          >
            {rangeText()}
          </Typography>
        )}

        <Stack direction="row" spacing={1.125} sx={{ pt: 0.5 }}>
          <ButtonBase
            onClick={() => move(-1)}
            disabled={atMin}
            aria-label={getTrans('guestForm.number.decrease')}
            data-testid="form-number-decrease"
            sx={signalNudgeButton}
          >
            <RemoveIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          </ButtonBase>
          <ButtonBase
            onClick={() => move(1)}
            disabled={atMax}
            aria-label={getTrans('guestForm.number.increase')}
            data-testid="form-number-increase"
            sx={signalNudgeButton}
          >
            <AddIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          </ButtonBase>
        </Stack>
      </Stack>
    </Stack>
  );
};
